import { API_BASE_URL } from '../services/api'

// Stale chunk hash 자동 복구.
// 새 배포가 나가면 Vite 가 매번 chunk 파일 hash 를 새로 박는데, 옛 index.html 을 캐시한
// 브라우저는 사라진 hash 의 chunk 를 import 하려다 "Failed to fetch dynamically imported
// module" 로 죽는다. 한 번만 자동 새로고침해서 새 index.html 을 가져온다.
// sessionStorage 가드로 새로고침 후에도 같은 에러가 나면 무한 reload 루프를 피한다.
const CHUNK_RELOAD_FLAG = 'infergraph.chunkReloadAttempted'

const isChunkLoadError = (reason: unknown): boolean => {
  if (!reason) return false
  const message =
    typeof reason === 'string'
      ? reason
      : (reason as { message?: string })?.message || ''
  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Importing a module script failed') ||
    message.includes('error loading dynamically imported module')
  )
}

const recoverFromStaleChunk = () => {
  let alreadyTried = false
  try {
    alreadyTried = sessionStorage.getItem(CHUNK_RELOAD_FLAG) === '1'
    sessionStorage.setItem(CHUNK_RELOAD_FLAG, '1')
  } catch {
    // sessionStorage 비활성화된 환경(시크릿 등) — guard 없이 1회 reload 시도.
  }
  if (alreadyTried) {
    console.error(
      '[chunk-recover] reload 후에도 동일 에러. 서버 측 배포 상태를 확인하세요.'
    )
    return
  }
  console.warn('[chunk-recover] stale chunk 감지 — 새 index.html 로 reload 합니다.')
  window.location.reload()
}

// 정상 페이지 로드 직후 reload 플래그 해제 — 다음 stale chunk 발생 시 다시 1회 시도 가능.
if (typeof window !== 'undefined') {
  window.addEventListener('load', () => {
    try {
      sessionStorage.removeItem(CHUNK_RELOAD_FLAG)
    } catch {
      /* ignore */
    }
  })
}

// 글로벌 에러 핸들러
export const setupGlobalErrorHandlers = () => {
  // Vite 가 lazy import 실패 시 발화하는 전용 이벤트.
  window.addEventListener('vite:preloadError', (event) => {
    console.warn('[chunk-recover] vite:preloadError 발화', event)
    recoverFromStaleChunk()
  })

  // React 에러 처리
  window.addEventListener('error', (event) => {
    if (isChunkLoadError(event.error) || isChunkLoadError(event.message)) {
      recoverFromStaleChunk()
      return
    }
    console.error('Global Error:', event.error)

    // 개발 환경에서만 자세한 로그
    if (import.meta.env.DEV) {
      console.error('Error details:', {
        message: event.error?.message,
        stack: event.error?.stack,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno
      })
    }
  })

  // Promise rejection 처리
  window.addEventListener('unhandledrejection', (event) => {
    if (isChunkLoadError(event.reason)) {
      recoverFromStaleChunk()
      return
    }
    console.error('Unhandled Promise Rejection:', event.reason)

    // API 에러인 경우 사용자에게 알림
    if (event.reason?.message?.includes('fetch') || event.reason?.message?.includes('Network')) {
      console.warn('네트워크 에러 발생. 백엔드 서버 상태를 확인하세요.')
    }
  })
}

// 개발 환경 설정
export const setupDevHelpers = () => {
  if (import.meta.env.DEV) {
    // 개발자 도구에서 사용할 수 있는 헬퍼 함수들
    (window as any).__DEV_HELPERS__ = {
      checkBackend: async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/health`)
          const data = await response.json()
          console.log('Backend Status:', data)
          return data
        } catch (error) {
          console.error('Backend not accessible:', error)
          return null
        }
      },
      
      clearCache: () => {
        localStorage.clear()
        sessionStorage.clear()
        console.log('Cache cleared')
      },
      
      reloadApp: () => {
        window.location.reload()
      }
    }
    
    console.log('🛠️ Dev helpers available: window.__DEV_HELPERS__')
  }
}